import type { AreaFeature } from "./apiTypes";
import { findContainingArea, type LatLngPoint } from "./pointInPolygon";

export type LocateState = "requesting" | "denied" | "unavailable" | "outside-area" | "success";

export interface LocateResult {
  state: LocateState;
  message: string;
  point?: LatLngPoint;
  area?: AreaFeature;
}

export interface LocateFlowOptions {
  positionProvider?: () => Promise<GeolocationPosition>;
  onState?: (result: LocateResult) => void;
}

export function requestCurrentPosition(options?: PositionOptions): Promise<GeolocationPosition> {
  if (!navigator.geolocation) {
    return Promise.reject({ code: 2, message: "Geolocation is unavailable" });
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

function messageForState(state: LocateState, area?: AreaFeature): string {
  switch (state) {
    case "requesting":
      return "Requesting your location...";
    case "denied":
      return "Location permission was denied.";
    case "unavailable":
      return "Location is unavailable on this device.";
    case "outside-area":
      return "Location found, but it is outside the mapped outage areas.";
    case "success": {
      const name = area?.properties.names?.[0] ?? area?.properties.label ?? area?.properties.key ?? "";
      return `Location found in ${name}.`;
    }
  }
}

function emit(options: LocateFlowOptions, result: LocateResult): void {
  options.onState?.(result);
}

export async function runLocateFlow(features: AreaFeature[], options: LocateFlowOptions = {}): Promise<LocateResult> {
  const requesting: LocateResult = { state: "requesting", message: messageForState("requesting") };
  emit(options, requesting);

  try {
    const position = await (options.positionProvider ?? (() => requestCurrentPosition()))();
    const point: LatLngPoint = [position.coords.latitude, position.coords.longitude];
    const area = findContainingArea(point, features);
    if (!area) {
      const result: LocateResult = { state: "outside-area", point, message: messageForState("outside-area") };
      emit(options, result);
      return result;
    }

    const result: LocateResult = { state: "success", point, area, message: messageForState("success", area) };
    emit(options, result);
    return result;
  } catch (error) {
    const state: LocateState = (error as { code?: number }).code === 1 ? "denied" : "unavailable";
    const result: LocateResult = { state, message: messageForState(state) };
    emit(options, result);
    return result;
  }
}
